import type { QuestionConfig } from '../types/findrisc';
import { LOINC_FINDRISC_QUESTIONNAIRE } from './loinc-questionnaire';

// Convert LOINC questionnaire to UI-friendly format
export const findiriscQuestions: QuestionConfig[] = [
  {
    id: 'age',
    type: 'choice',
    title: 'What is your age?',
    description: 'Select the age range that applies to you.',
    required: true,
    linkId: '124299',
    options: [
      { value: 'LA31987-3', label: 'Under 45 years', points: 0 },
      { value: 'LA31988-1', label: '45-54 years', points: 2 },
      { value: 'LA31989-9', label: '55-64 years', points: 3 },
      { value: 'LA31990-7', label: 'Over 64 years', points: 4 }
    ]
  },
  {
    id: 'sex',
    type: 'choice',
    title: 'What is your biological sex?',
    description: 'This helps determine the appropriate waist measurement categories.',
    required: true,
    linkId: 'sex', // Special handling for UI
    options: [
      { value: 'male', label: 'Male', points: 0 },
      { value: 'female', label: 'Female', points: 0 }
    ]
  },
  {
    id: 'bmi',
    type: 'bmi-calculator',
    title: 'What is your Body Mass Index (BMI)?',
    description: 'Use your height and weight to calculate your Body Mass Index (BMI).',
    required: true,
    linkId: '124240',
    options: [
      { value: 'LA31984-0', label: 'Under 25 kg/m²', points: 0 },
      { value: 'LA31985-7', label: '25–30 kg/m²', points: 1 },
      { value: 'LA31986-5', label: 'Over 30 kg/m²', points: 3 }
    ]
  },
  {
    id: 'waistCircumference',
    type: 'choice',
    title: 'What is your waist circumference?',
    description: 'Measure at the navel level.',
    required: true,
    linkId: '124239',
    enableWhen: {
      question: 'sex',
      answerCode: 'male'
    },
    options: [
      { value: 'LA31920-4', label: 'Under 94 cm (~37 inches)', points: 0 },
      { value: 'LA31921-2', label: '94-102 cm (~37-40 inches)', points: 3 },
      { value: 'LA31922-0', label: 'Over 102 cm (~40 inches)', points: 4 }
    ]
  },
  {
    id: 'waistCircumferenceFemale',
    type: 'choice',
    title: 'What is your waist circumference?',
    description: 'Measure at the navel level.',
    required: true,
    linkId: '124239',
    enableWhen: {
      question: 'sex',
      answerCode: 'female'
    },
    options: [
      { value: 'LA31923-8', label: 'Under 80 cm (~31 inches)', points: 0 },
      { value: 'LA31924-6', label: '80-88 cm (~31-35 inches)', points: 3 },
      { value: 'LA31925-3', label: 'Over 88 cm (~35 inches)', points: 4 }
    ]
  },
  {
    id: 'physicalActivity',
    type: 'boolean',
    title: 'Do you usually have daily at least 30 min of physical activity at work and/or during leisure time?',
    description: 'This includes work, leisure activities, or structured exercise.',
    required: true,
    linkId: '124238',
    options: [
      { value: 'LA33-6', label: 'Yes', points: 0 },
      { value: 'LA32-8', label: 'No', points: 2 }
    ]
  },
  {
    id: 'vegetablesFruits',
    type: 'boolean',
    title: 'How often do you eat vegetables, fruit, or berries?',
    description: 'Please select the option that best reflects your usual eating habits.',
    required: true,
    linkId: '124237',
    options: [
      { value: 'LA14799-3', label: 'Every day', points: 0 },
      { value: 'LA31926-1', label: 'Not every day', points: 1 }
    ]
  },
  {
    id: 'bloodPressureMeds',
    type: 'boolean',
    title: 'Have you ever taken anti-hypertensive medication regularly?',
    description: 'Choose the option that describes your history with blood pressure medication.',
    required: true,
    linkId: '124301',
    options: [
      { value: 'LA32-8', label: 'No', points: 0 },
      { value: 'LA33-6', label: 'Yes', points: 2 }
    ]
  },
  {
    id: 'highBloodGlucose',
    type: 'boolean',
    title: 'Have you ever been found to have high blood glucose?',
    description: 'This includes during medical exams, illness, or pregnancy (gestational diabetes).',
    required: true,
    linkId: '124236',
    options: [
      { value: 'LA32-8', label: 'No', points: 0 },
      { value: 'LA33-6', label: 'Yes', points: 5 }
    ]
  },
  {
    id: 'familyHistory',
    type: 'choice',
    title: 'Have any of the members of your immediate family or other relatives been diagnosed with diabetes?',
    description: 'Consider only blood relatives diagnosed with type 1 or type 2 diabetes.',
    required: true,
    linkId: '124235',
    options: [
      { value: 'LA32-8', label: 'No family history', points: 0 },
      { value: 'LA31927-9', label: 'Grandparent, aunt, uncle, or first cousin', points: 3 },
      { value: 'LA31928-7', label: 'Parent, sibling, or child', points: 5 }
    ]
  }
];